import {EditorView, basicSetup} from "codemirror"
import {EditorState, Prec} from "@codemirror/state"
import {keymap, placeholder} from "@codemirror/view"
import {oneDark} from "@codemirror/theme-one-dark"
import {colorPreviews} from "../branding/color_previews"
import {css} from "@codemirror/lang-css"
import {syntaxTree} from "@codemirror/language"
import {compileStylesheet, MAX_BYTES} from "../branding/stylesheet"
import {t} from "../i18n"
import {stylesheetTokens, officialStylesheet} from "../branding/tokens"
import {insertToken} from "../branding/token_insertion"

export default {
  mounted() {
    this.source = this.el.querySelector("[data-css-source]")
    this.editorHost = this.el.querySelector("[data-css-editor]")
    this.form = this.el.querySelector("form")
    this.revision = this.el.dataset.cssRevision
    this.timer = null
    this.syntaxError = null
    this.compileError = null
    this.ignoreChange = false
    this.destroyedFlag = false
    if (!this.source || !this.editorHost) return

    this.editor = new EditorView({
      parent: this.editorHost,
      state: EditorState.create({
        doc: this.source.value,
        extensions: [basicSetup, css(), oneDark, colorPreviews, EditorView.lineWrapping,
          placeholder(t("Write your CSS here, import a file or start with an example.")),
          Prec.highest(keymap.of([{key: "Mod-Enter", run: () => { this.form.requestSubmit(); return true }}])),
          EditorView.contentAttributes.of({
            "aria-label": this.source.getAttribute("aria-label") || "CSS",
            "aria-describedby": this.source.getAttribute("aria-describedby") || "custom-stylesheet-client-status",
          }),
          EditorView.theme({
            "&": {border:"1px solid var(--border-default)", borderRadius:"8px", fontSize:"12px"},
            ".cm-scroller": {minHeight:"360px", maxHeight:"560px", overflow:"auto", fontFamily:"ui-monospace, SFMono-Regular, monospace"},
            ".cm-content": {padding:"12px 0"},
          }),
          EditorView.updateListener.of(update => {
            if (!update.docChanged || this.ignoreChange) return
            this.source.value = update.state.doc.toString()
            const mode = this.el.querySelector('[name="custom_stylesheet[mode]"]')
            if (mode) mode.value = "custom"
            this.scheduleChange()
          }),
        ],
      }),
    })
    this.source.hidden = true
    this.el.dataset.editorReady = "true"
    // A form listener ensures Ctrl+Enter / a fast click saves the latest draft,
    // even before the typing debounce has elapsed.
    this.onSubmit = event => {
      this.flushChange()
      if (this.mode() === "custom" && this.compileError) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.editor.focus()
      }
    }
    this.onChange = event => {
      if (!event.target.matches("[data-css-import]")) this.validateDraft()
    }
    this.onClick = event => {
      if (event.target.closest(`label[for="${this.source.id}"]`)) {
        event.preventDefault()
        this.editor.focus()
      }
      const action = event.target.closest("[data-css-export], [data-css-example], [data-css-reset]")
      if (!action || !this.el.contains(action)) return
      event.preventDefault()
      if (action.hasAttribute("data-css-export")) this.exportCSS()
      if (action.hasAttribute("data-css-example")) this.replaceCSS(this.officialCSS || "", "custom")
      if (action.hasAttribute("data-css-reset")) {
        const scope = this.el.querySelector('[name="custom_stylesheet[scope]"]')
        if (scope) scope.value = this.el.dataset.savedScope || "embed"
        const priority = this.el.querySelector('[name="custom_stylesheet[priority]"]')
        if (priority) priority.value = this.el.dataset.savedPriority || "override"
        this.replaceCSS(this.el.dataset.savedCss || "", this.el.dataset.savedMode || "inherit")
      }
    }
    this.onImport = event => this.importCSS(event)
    this.onTokenSearch = () => this.renderTokens()
    this.el.querySelector('[data-css-token-search]')?.addEventListener('input', this.onTokenSearch)
    this.form.addEventListener("submit", this.onSubmit, true)
    this.el.addEventListener("change", this.onChange)
    this.el.addEventListener("click", this.onClick)
    this.el.querySelector("[data-css-import]")?.addEventListener("change", this.onImport)
    this.tokens = stylesheetTokens(document)
    this.officialCSS = officialStylesheet(document)
    this.renderTokens()
    this.updateActions()
    this.validateDraft()
  },

  renderTokens() {
    const list = this.el.querySelector('[data-css-tokens]')
    if (!list) return
    const query = (this.el.querySelector('[data-css-token-search]')?.value || '').toLowerCase().trim()
    const tokens = (this.tokens || []).filter(token => `${token.name} ${token.selector} ${token.value} ${token.conditions.join(' ')}`.toLowerCase().includes(query))
    const fragment = document.createDocumentFragment()
    for (const token of tokens) {
      const row = document.createElement('div')
      row.className = 'branding-token-entry'
      const description = document.createElement('div')
      description.className = 'branding-token-description'
      const name = document.createElement('code')
      name.textContent = token.name
      const detail = document.createElement('small')
      detail.textContent = `${token.value} · ${[...token.conditions, token.selector].join(' / ')}`
      description.append(name, detail)
      const insert = document.createElement('button')
      insert.type = 'button'
      insert.className = 'seiva-btn seiva-btn--secondary seiva-btn-size--sm'
      insert.textContent = t('Insert token')
      insert.setAttribute('aria-label', `${t('Insert token')} ${token.name}`)
      insert.addEventListener('click', () => {
        const source = this.editor.state.doc.toString()
        const insertion = insertToken(source, token)
        if (insertion.source !== source) this.replaceCSS(insertion.source, 'custom')
        this.editor.dispatch({selection: {anchor: insertion.from, head: insertion.to}, scrollIntoView: true})
        this.editor.focus()
      })
      row.append(description, insert)
      fragment.append(row)
    }
    list.replaceChildren(fragment)
    const count = this.el.querySelector('[data-css-token-count]')
    if (count) count.textContent = t('%{count} tokens found', {count: tokens.length})
  },

  mode() { return this.el.querySelector('[name="custom_stylesheet[mode]"]')?.value || "inherit" },

  scheduleChange() {
    clearTimeout(this.timer)
    // Large imported stylesheets are parsed only after a short typing pause.
    this.timer = setTimeout(() => this.flushChange(), 250)
  },

  updateActions() {
    const example = this.el.querySelector('[data-css-example]')
    if (example) {
      example.hidden = this.editor.state.doc.length > 0
      example.disabled = !this.officialCSS
    }
  },

  flushChange() {
    clearTimeout(this.timer)
    this.updateActions()
    this.source.value = this.editor.state.doc.toString()
    if (new TextEncoder().encode(this.source.value).length > (Number(this.el.dataset.maxBytes) || MAX_BYTES)) {
      this.compileError = t("The stylesheet exceeds 256 KB.")
      this.status(this.compileError, true)
      return
    }
    this.source.dispatchEvent(new Event("input", {bubbles:true}))
    this.validateDraft()
  },

  replaceCSS(value, mode) {
    this.ignoreChange = true
    this.editor.dispatch({changes:{from:0, to:this.editor.state.doc.length, insert:value}})
    this.ignoreChange = false
    const select = this.el.querySelector('[name="custom_stylesheet[mode]"]')
    if (select && mode) select.value = mode
    this.flushChange()
  },

  async importCSS(event) {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return
    try {
      if (!/\.css$/i.test(file.name)) throw new Error(t("Choose a .css file."))
      if (file.size > (Number(this.el.dataset.maxBytes) || MAX_BYTES)) throw new Error(t("The stylesheet exceeds 256 KB."))
      const value = new TextDecoder("utf-8", {fatal:true}).decode(await file.arrayBuffer())
      if (!this.destroyedFlag) this.replaceCSS(value, "custom")
    } catch (error) { this.status(t(error.message), true) }
    finally { input.value = "" }
  },

  exportCSS() {
    const mode = this.mode()
    const source = mode === "inherit" ? this.el.dataset.inheritedCss || "" : this.editor.state.doc.toString()
    const url = URL.createObjectURL(new Blob([source], {type:"text/css;charset=utf-8"}))
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = "workspace-branding.css"
    document.body.append(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  },

  status(message, error = false) {
    const status = this.el.querySelector("[data-css-status]")
    if (status) {
      status.textContent = message
      status.dataset.error = String(error)
      status.style.color = error ? "var(--error)" : ""
    }
    this.editor?.contentDOM.setAttribute("aria-invalid", String(error))
  },

  validateDraft() {
    if (!this.editor) return
    const mode = this.mode()
    const source = mode === "custom" ? this.editor.state.doc.toString() : mode === "inherit" ? this.el.dataset.inheritedCss || "" : ""
    this.syntaxError = null
    this.compileError = null
    if (mode === "custom") {
      const tree = syntaxTree(this.editor.state)
      tree.iterate({enter: node => {
        if (node.type.isError && !this.syntaxError) {
          const line = this.editor.state.doc.lineAt(node.from).number
          this.syntaxError = t("Check the CSS syntax near line %{line}.", {line})
        }
      }})
    }
    try {
      const priority = mode === "inherit" ? this.el.dataset.inheritedPriority || "override" :
        this.el.querySelector('[name="custom_stylesheet[priority]"]')?.value || "override"
      const result = compileStylesheet(source, window, priority)
      if (this.syntaxError) this.status(this.syntaxError)
      else this.status(t("%{count} CSS declarations · changes apply after saving", {count:result.declarations}))
    } catch (error) {
      this.compileError = error.message
      this.status(t(error.message), true)
    }
  },

  beforeUpdate() {
    this.disclosures = Array.from(this.el.querySelectorAll('details[id]')).map(el => [el.id, el.open])
  },

  updated() {
    for (const [id, open] of this.disclosures || []) {
      const disclosure = this.el.querySelector(`#${id}`)
      if (disclosure) disclosure.open = open
    }
    // phx-update=ignore protects the editor from server echo during typing.
    // Only an explicit server revision (save/reset) may replace its document.
    if (this.editor && this.revision !== this.el.dataset.cssRevision) {
      this.revision = this.el.dataset.cssRevision
      this.ignoreChange = true
      this.editor.dispatch({changes:{from:0, to:this.editor.state.doc.length, insert:this.el.dataset.savedCss || ""}})
      this.source.value = this.editor.state.doc.toString()
      this.ignoreChange = false
    }
    this.updateActions()
    this.validateDraft()
  },

  destroyed() {
    this.destroyedFlag = true
    clearTimeout(this.timer)
    this.form?.removeEventListener("submit", this.onSubmit, true)
    this.el.removeEventListener("change", this.onChange)
    this.el.removeEventListener("click", this.onClick)
    this.el.querySelector("[data-css-token-search]")?.removeEventListener("input", this.onTokenSearch)
    this.el.querySelector("[data-css-import]")?.removeEventListener("change", this.onImport)
    this.editor?.destroy()
  },
}
